/**
 * Prop Studio — `/sandbox/props`.
 *
 * Where the photoreal prop library is made (scripts/props/README.md). Pick a
 * space type and one of its FLOOR props; the viewer shows the procedural prop
 * the designer draws, its kit box, the wall it stands against, and the
 * candidate model from the library; the right column finds a model
 * (BlenderKit, an asset id, or a file of your own), places it — scale, turn,
 * offset, mirror, with the three fits as starting values — and saves the
 * numbers to the library. The Blender renderer places the model from those
 * same numbers.
 *
 * Everything the page does goes through the helper service
 * (components/props/studioApi.ts); without it running, the page still shows
 * the procedural props and says how to start it. The library is re-read every
 * ten seconds while the tab is visible and whenever the window gets focus, so
 * a model.blend edited in Blender shows as stale and a helper started late is
 * noticed, without a reload.
 *
 * Open to anyone under the dev server; super-admin only in a built app (the
 * route guard lives in App.tsx). Sandbox-first: nothing here touches the
 * estimator.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, DraftingCompass } from 'lucide-react';
import { designerControlStyle as control } from '../components/designer/DesignerHost';
import { STAGING_KITS, PropDef, Placement } from '../calculations/staging/kits';
import { Catalogue, CatalogueEntry, FIXTURES, asPlaced, kitWith, kitsWith, newPropId, placesOf, propById } from '../calculations/staging/catalogue';
import { PlacesPanel } from '../components/props/PlacesPanel';
import { PropViewer, CandidateState, ViewerHandle, ViewerMode, ViewerPart } from '../components/props/PropViewer';
import { CandidatePanel, LibraryBusy } from '../components/props/CandidatePanel';
import { PlacementPanel } from '../components/props/PlacementPanel';

import {
  AssignAnswer, Library, LibrarySource, OriginRefusedError, PROPS_BACKEND, ServiceDownError,
  assignModel, getLibrary, helperOutOfDate, previewUrl, propStatus, reconvertModel, removeCatalogue, removeProp, saveCatalogue, saveTransform, uploadModel, ModelUnits,
} from '../components/props/studioApi';
import { DEFAULT_TRANSFORM, PropTransform, Vec3, placedBoundsFeet, sameTransform, stagedSizeFeet } from '../components/props/placement';
import { isPartKey, nextPartKey, nextVariantKey, partKeys, partNumber, propStatusAcross, variantKeys, variantNumber } from '../components/props/variants';
import { propFrame, stagedFrame } from '../components/props/propFrame';
import { PROP_STUDIO_STRINGS as S } from '../components/props/strings';
import { FinishesManager } from '../components/props/FinishesManager';
import { LookLab } from '../components/props/LookLab';

/** Whether the viewer draws the hold and hang markers (localStorage). */
const MARKERS_KEY = 'propStudio.markers';

// The spaces, then every prop (made ones too, and any in no space), then the fixtures (the opener's motor).
/** Which section the studio last showed. */
const SECTION_KEY = 'propStudio.section';
type Section = 'props' | 'finishes' | 'looks';
const KIT_KEYS = [...Object.keys(STAGING_KITS), 'all', 'fixtures'];
const DEFAULT_KIT = 'garage';
const ACCENT = '#FEB100';
const MUTED = '#9a9ca6';
/** How often the library is re-read while the tab is visible. */
const LIBRARY_POLL_MS = 10_000;
const LABEL: React.CSSProperties = { fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, margin: '4px 0 6px' };
const BADGE_COLOURS: Record<'none' | 'assigned' | 'stale', { fg: string; border: string }> = {
  none: { fg: MUTED, border: 'rgba(255,255,255,0.2)' },
  assigned: { fg: '#9fe0b0', border: '#9fe0b0' },
  stale: { fg: ACCENT, border: ACCENT },
};

/**
 * The props listed for a space: its kit with the catalogue applied
 * (staging/catalogue.ts); under All props, every prop there is, made ones and
 * those in no space included; under Fixtures, the garage opener's motor.
 * Every one takes a model: floor props stand, the rest are held (propFrame.ts).
 */
function studioProps(kit: string, catalogue?: Catalogue): PropDef[] {
  if (kit === 'fixtures') return FIXTURES;
  if (kit === 'all') {
    const seen = new Map<string, PropDef>();
    for (const k of Object.values(kitsWith(catalogue))) for (const p of k.props) if (!seen.has(p.id)) seen.set(p.id, p);
    for (const [id, e] of Object.entries(catalogue ?? {})) {
      const made = e.added ? propById(id, catalogue) : undefined;
      if (made && !seen.has(id)) seen.set(id, made);
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }
  return kitWith(kit, catalogue).props;
}
const kitName = (kit: string): string => (kit === 'all' ? S.allProps : kit === 'fixtures' ? S.fixtures : S.spaceTypeName[kit] ?? kit);
const isFixture = (id: string | undefined): boolean => !!id && FIXTURES.some((f) => f.id === id);
const tidy = (n: number): number => Math.round(n * 1000) / 1000;

/** The notice line. `helper`: it is about reaching the helper, and clears by itself once the helper answers. */
type Notice = { kind: 'error' | 'info' | 'ok'; text: string; helper?: boolean } | null;

/** A prop (or kit, or variant, or part) switch waiting on the unsaved-placement prompt. */
interface PendingSwitch { kit: string; propId: string | null; variant?: string | null; part?: string | null }

/** What the headless driver (scripts/headless) can read and steer, in dev. */
interface StudioDebug {
  state: () => unknown;
  placedBounds: (propId?: string) => unknown;
  /** The candidate as the viewer draws it now (its object's own position / turn / scale), for checking against placement.ts. */
  candidatePlacement: () => unknown;
  /** A point of the model (its own frame, Blender axes, metres) on screen, and a spot on its surface facing the camera: for a test to click the model as a user would. */
  screenOfModelPoint: (p: [number, number, number]) => { x: number; y: number } | null;
  frontModelVertex: () => [number, number, number] | null;
  /** A part as the viewer draws it in the model's frame (null until its preview is loaded). */
  partPlacement: (key: string) => unknown;
  setTransform: (patch: Partial<PropTransform>) => void;
  /** The drawn model's lowest point and nearest point to the wall (feet, the prop's frame), for a test to hold Rest on floor / Flush to wall to. */
  modelExtremes: () => { minY: number; minZ: number } | null;
  /** Switches without the unsaved-placement prompt: a script that calls it means it. A variant key (`bike~2`) picks that model of the prop; a part key (`bike+1`) that part of it. */
  select: (kit: string, propId?: string, variant?: string, part?: string) => void;
}

/** What the previous model was, for the notice after a replace: its name, and the asset id to paste to get a BlenderKit one back. */
const replacedWords = (prev: LibrarySource | undefined): { name: string; asset: string | undefined } | null => (
  prev ? { name: prev.name || prev.asset || 'the previous model', asset: prev.kind === 'blenderkit' ? prev.asset : undefined } : null
);

export const PropStudio: React.FC = () => {
  const [spaceType, setSpaceType] = useState(DEFAULT_KIT);
  const [propId, setPropId] = useState(() => studioProps(DEFAULT_KIT)[0]?.id ?? '');
  // Which of the prop's models (variants) is up: the library key — the prop's
  // own id for the first, `<id>~n` for the others; null = the prop's own.
  const [variant, setVariant] = useState<string | null>(null);
  // A part of the chosen model being placed (its key, `<model>+n`), or null: the model itself.
  const [part, setPart] = useState<string | null>(null);
  const [library, setLibrary] = useState<Library | null>(null);
  const [draft, setDraft] = useState<PropTransform>(DEFAULT_TRANSFORM);
  const [mode, setMode] = useState<ViewerMode>('side');
  // Which part of the studio: the props' models, the floors and lighting by space, or the
  // Look Lab (remembered here, or ?section=finishes|looks; ?estimate=<guid> opens the Lab on that room).
  const [section, setSection] = useState<Section>(() => {
    const params = new URLSearchParams(window.location.search);
    const asked = params.get('estimate') ? 'looks' : params.get('section');
    if (asked === 'finishes' || asked === 'looks') return asked;
    try { const kept = window.localStorage.getItem(SECTION_KEY); return kept === 'finishes' || kept === 'looks' ? kept : 'props'; } catch { return 'props'; }
  });
  const chooseSection = (k: Section) => { setSection(k); try { window.localStorage.setItem(SECTION_KEY, k); } catch { /* not kept — fine */ } };
  const [busy, setBusy] = useState<LibraryBusy>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [candidate, setCandidate] = useState<CandidateState>({ phase: 'none' });
  const [pendingSwitch, setPendingSwitch] = useState<PendingSwitch | null>(null);
  const viewerRef = useRef<ViewerHandle>(null);

  const catalogue = library?.catalogue;
  const catalogueRef = useRef(catalogue);
  catalogueRef.current = catalogue;
  const listed = useMemo(() => studioProps(spaceType, catalogue), [spaceType, catalogue]);
  // The chosen prop, or the list's first when the chosen one is not in it.
  const def: PropDef | undefined = listed.find((p) => p.id === propId) ?? listed[0];
  const fixture = isFixture(def?.id);
  // The ways it can go (best first), and the one the viewer shows it placed: the first, unless another is picked.
  const [previewPlace, setPreviewPlace] = useState<Placement | undefined>(undefined);
  useEffect(() => { setPreviewPlace(undefined); }, [def?.id]);
  const places = def ? placesOf(def) : [];
  const shownPlace = previewPlace && places.includes(previewPlace) ? previewPlace : places[0];
  const shownDef: PropDef | undefined = def && shownPlace && def.kind !== 'wall' ? asPlaced(def, shownPlace) : def;
  const firstDef: PropDef | undefined = def && places[0] && def.kind !== 'wall' ? asPlaced(def, places[0]) : def;
  // Which point of the model goes to the prop's origin is its first place's
  // (a floor prop's floor-centre, a hung prop's top-centre): saved with the
  // placement for renders of exports that do not say how each prop was
  // placed; the viewer shows the point of the place being previewed.
  const frameAnchor = firstDef ? propFrame(firstDef).anchor : 'floor';
  const shownAnchor = shownDef ? propFrame(shownDef).anchor : 'floor';
  // The library key everything below works on: the chosen variant of the prop.
  const variants = def ? variantKeys(library, def.id) : [];
  // With no choice made, the prop's own model — or, when that one is gone and
  // others remain (Model 1 removed, Model 2 kept), the first that remains:
  // never an empty slot beside the models the prop has.
  const chosen = variant && variant.startsWith(`${def?.id ?? ''}~`) ? variant : null;
  // The model in hand (a variant of the prop) …
  const ownerKey = def ? (chosen ?? (library?.props[def.id] || variants.length === 0 ? def.id : variants[0])) : '';
  const ownerEntry = def ? library?.props[ownerKey] : undefined;
  const parts = partKeys(library, ownerKey);
  // … and what is being placed: the model itself, or one of its parts.
  const partKey = part && part.startsWith(`${ownerKey}+`) ? part : null;
  const entryKey = partKey ?? ownerKey;
  const entry = def ? library?.props[entryKey] : undefined;
  // A part stands by its bottom in the model's frame (Offset Up sets it on the bench).
  const anchor = partKey ? 'floor' : frameAnchor;
  const saved = entry?.transform ?? DEFAULT_TRANSFORM;
  const savedKey = JSON.stringify(saved);
  // Against the draft AS IT WOULD BE SAVED (with the prop's anchor): a held
  // prop whose library entry lacks the anchor is unsaved until it is written.
  const dirty = !sameTransform({ ...draft, anchor }, saved);
  // Unsaved work worth a prompt: a placement there is a model to place.
  const hasUnsaved = dirty && !!entry?.modelBounds;
  const candidateUrl = def && entry?.status?.hasGlb ? previewUrl(entryKey, entry) : null;
  // What the viewer draws: the model with its parts; the one being placed follows the draft.
  const ownerUrl = def && ownerEntry?.status?.hasGlb ? previewUrl(ownerKey, ownerEntry) : null;
  const viewerParts: ViewerPart[] = parts.map((k) => {
    const e = library?.props[k];
    const selected = k === entryKey;
    return { key: k, url: e?.status?.hasGlb ? previewUrl(k, e) : null, modelBounds: e?.modelBounds ?? null, transform: selected ? { ...draft, anchor: 'floor' } : e?.transform ?? DEFAULT_TRANSFORM, selected };
  });
  const modelBounds = entry?.modelBounds ?? null;
  const modelIsBlend = !!entry?.files?.model && /\.blend$/i.test(entry.files.model);
  // The model's size as the draft places it, for the "Match the model" row (the size itself is worked out when the button is pressed).
  const modelSize = modelBounds && !partKey ? (() => { const b = placedBoundsFeet(modelBounds, { ...draft, anchor: shownAnchor }); return { w: b.max[0] - b.min[0], d: b.max[2] - b.min[2], h: b.max[1] - b.min[1] }; })() : null;

  // The draft starts from what is saved: on another prop always; on the same
  // prop when the saved numbers change under it (a Save, an assign that kept
  // them) — unless the draft is dirty, which means library.json changed under
  // unsaved work (edited elsewhere): that work is kept; Save overwrites, Reset
  // takes the new numbers.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const lastPropRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const switched = lastPropRef.current !== entryKey;
    lastPropRef.current = entryKey;
    if (switched || !dirtyRef.current) setDraft(JSON.parse(savedKey) as PropTransform);
  }, [entryKey, savedKey]);

  const noticeFor = useCallback((err: unknown): Notice => {
    if (err instanceof ServiceDownError) return { kind: 'error', text: PROPS_BACKEND === 'php' ? S.notice.serviceDownPhp : S.notice.serviceDown, helper: true };
    if (err instanceof OriginRefusedError) return { kind: 'error', text: S.notice.originRefused(err.origin), helper: true };
    return { kind: 'error', text: err instanceof Error ? err.message : String(err) };
  }, []);
  const report = useCallback((err: unknown) => setNotice(noticeFor(err)), [noticeFor]);

  // Reads of the library can overtake one another (a poll in flight while a
  // Save's own re-read lands): a read is applied only when no later one has
  // been. A helper notice clears the moment any read succeeds.
  const reads = useRef({ started: 0, applied: 0 });
  const refresh = useCallback(async (): Promise<Library> => {
    const n = ++reads.current.started;
    const lib = await getLibrary();
    if (n > reads.current.applied) {
      reads.current.applied = n;
      setLibrary((old) => (old && JSON.stringify(old) === JSON.stringify(lib) ? old : lib));
      // A helper from before a code change answers, but will refuse what
      // this page sends: say so, loudly, until a restarted one answers.
      setNotice((cur) => (helperOutOfDate(lib)
        ? { kind: 'error', text: PROPS_BACKEND === 'php' ? S.notice.helperOutOfDatePhp : S.notice.helperOutOfDate, helper: true }
        : cur?.helper ? null : cur));
    }
    return lib;
  }, []);

  // On open, every LIBRARY_POLL_MS while the tab is visible, on focus and on
  // becoming visible again: edits made in Blender (status.stale, source.edited)
  // and a helper started after the page opened show up without a reload. A
  // failing poll says so once, not every ten seconds.
  useEffect(() => {
    let stopped = false;
    const read = async (initial: boolean) => {
      if (stopped || (!initial && document.visibilityState !== 'visible')) return;
      try {
        await refresh();
      } catch (err) {
        if (stopped) return;
        const next = noticeFor(err);
        setNotice((cur) => (cur && next && cur.text === next.text ? cur : next));
      }
    };
    read(true);
    const timer = window.setInterval(() => read(false), LIBRARY_POLL_MS);
    const onFocus = () => read(false);
    const onVisibility = () => { if (document.visibilityState === 'visible') read(false); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, noticeFor]);

  // Picking the hang point on the model: ends with a pick, Esc, or whenever another model is in hand.
  const [picking, setPicking] = useState(false);
  useEffect(() => { setPicking(false); }, [entryKey]);
  const isHeld = !!shownDef && propFrame(shownDef).kind === 'held' && !partKey && !fixture;

  // Leaving the page with unsaved placement: the browser asks.
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  /** The switch itself: the kit, and the prop when the kit has it, else the kit's first floor prop. */
  const applySwitch = useCallback((nextKit: string, nextProp: string | null, nextVariant: string | null = null, nextPart: string | null = null) => {
    setSpaceType(nextKit);
    setPropId((current) => {
      const wanted = nextProp ?? current;
      const list = studioProps(nextKit, catalogueRef.current);
      return list.some((p) => p.id === wanted) ? wanted : list[0]?.id ?? '';
    });
    setVariant(nextVariant);
    setPart(nextPart);
  }, []);
  /** A switch from the controls: straight away, or held behind the prompt while there is unsaved placement. */
  const requestSwitch = (nextKit: string, nextProp: string | null, nextVariant: string | null = null, nextPart: string | null = null) => {
    if (nextKit === spaceType && (nextProp === null || nextProp === def?.id) && nextVariant === variant && nextPart === partKey) return;
    if (hasUnsaved) { setPendingSwitch({ kit: nextKit, propId: nextProp, variant: nextVariant, part: nextPart }); return; }
    applySwitch(nextKit, nextProp, nextVariant, nextPart);
  };

  /** A change to the library: do it, read the library back, say how it went. True when it worked. */
  const mutate = useCallback(async (kind: LibraryBusy, work: () => Promise<unknown>, during: Notice, done: (lib: Library, answer: unknown) => string | null): Promise<boolean> => {
    setBusy(kind);
    setNotice(during);
    try {
      const answer = await work();
      const lib = await refresh();
      const text = done(lib, answer);
      setNotice(text ? { kind: 'ok', text } : null);
      return true;
    } catch (err) {
      report(err);
      // The helper may have written a half entry before failing (downloaded,
      // conversion failed: stale, with its Reconvert): show what is there now.
      try { await refresh(); } catch { /* the notice already says the helper is unreachable */ }
      return false;
    } finally {
      setBusy(null);
    }
  }, [refresh, report]);

  const assign = (asset: string, resolution: string) => {
    if (!def) return;
    const id = entryKey;
    mutate('assign', () => assignModel(id, asset, resolution), { kind: 'info', text: S.search.busy }, (lib, answer) => {
      const name = lib.props[id]?.source?.name || asset;
      const prev = replacedWords((answer as AssignAnswer | undefined)?.replaced?.source);
      return prev ? S.search.assignedReplacing(name, prev.name, prev.asset) : S.search.assigned(name);
    });
  };
  // ---- The catalogue (staging/catalogue.ts): saved at once, the library re-read.
  const [catBusy, setCatBusy] = useState(false);
  const saveEntry = async (next: CatalogueEntry) => {
    if (!def) return;
    setCatBusy(true);
    try {
      await saveCatalogue(def.id, next);
      await refresh();
      setNotice({ kind: 'ok', text: S.catalogue.saved });
    } catch (err) {
      report(err);
    } finally {
      setCatBusy(false);
    }
  };
  /**
   * "Match the model": the kit box becomes the model's size. A floor prop's
   * box is as staging sees the model (from the floor to its top). A HELD prop
   * (on the wall, on an accessory) is first moved onto the hold point — its
   * bottom (or its top, when it hangs) at the gold ball, centred along the
   * wall and in depth — and the box is sized round it; a wall prop's hold
   * point is set so the box's back is on the wall. Saved: the placement and the entry.
   */
  const matchModel = async () => {
    if (!def || !modelBounds || partKey) return;
    const t: PropTransform = { ...draft, anchor };
    const placed = placedBoundsFeet(modelBounds, t);
    const clamp = (v: number) => Math.min(40, Math.max(0.05, tidy(v)));
    const w = placed.max[0] - placed.min[0], d = placed.max[2] - placed.min[2], h = placed.max[1] - placed.min[1];
    const held = propFrame(firstDef ?? def).kind === 'held';
    const catEntry = catalogue?.[def.id];
    setCatBusy(true);
    setSaving(true);
    try {
      if (!held) {
        const s = stagedSizeFeet(placed, t);
        await saveCatalogue(def.id, { ...(catEntry ?? {}), size: { w: clamp(s.w), d: clamp(s.d), h: clamp(s.h) } });
      } else {
        const cx = (placed.min[0] + placed.max[0]) / 2, cz = (placed.min[2] + placed.max[2]) / 2;
        const dy = anchor === 'top' ? placed.max[1] : placed.min[1];
        const next: PropTransform = { ...draft, offset: [tidy(draft.offset[0] - cx), tidy(draft.offset[1] - dy), tidy(draft.offset[2] - cz)] as Vec3 };
        setDraft(next);
        await saveTransform(entryKey, { ...next, anchor });
        await saveCatalogue(def.id, {
          ...(catEntry ?? {}), size: { w: clamp(w), d: clamp(d), h: clamp(h) }, holdX: 0,
          ...(def.kind === 'wall' ? { holdZ: Math.min(10, tidy(d / 2)) } : {}),
          ...(anchor === 'top' ? { drop: clamp(h) } : {}),
        });
      }
      await refresh();
      setNotice({ kind: 'ok', text: S.catalogue.matched });
    } catch (err) {
      report(err);
    } finally {
      setSaving(false);
      setCatBusy(false);
    }
  };
  const deleteProp = async () => {
    if (!def || !window.confirm(S.catalogue.removeConfirm(def.label))) return;
    setCatBusy(true);
    try {
      await removeCatalogue(def.id);
      await refresh();
      setNotice({ kind: 'ok', text: S.catalogue.removed(def.label) });
      setPropId('');
    } catch (err) {
      report(err);
    } finally {
      setCatBusy(false);
    }
  };
  const newProp = async () => {
    if (hasUnsaved) { setNotice({ kind: 'info', text: S.catalogue.saveFirst }); return; }
    const label = window.prompt(S.catalogue.newPropPrompt)?.trim().slice(0, 60);
    if (!label) return;
    const id = newPropId(label, (x) => !!catalogue?.[x] || !!library?.props[x]);
    setCatBusy(true);
    try {
      await saveCatalogue(id, { added: true, label, size: { w: 1, d: 1, h: 1 }, places: ['floor'], spaces: STAGING_KITS[spaceType] ? [spaceType] : [] });
      await refresh();
      if (!STAGING_KITS[spaceType]) setSpaceType('all');
      setPropId(id);
      setVariant(null);
      setPart(null);
      setNotice({ kind: 'ok', text: S.catalogue.made(label) });
    } catch (err) {
      report(err);
    } finally {
      setCatBusy(false);
    }
  };

  const upload = (file: File, units: ModelUnits) => {
    if (!def) return;
    const id = entryKey;
    mutate('upload', () => uploadModel(id, file, units), { kind: 'info', text: S.upload.busy }, (_lib, answer) => {
      const prev = replacedWords((answer as AssignAnswer | undefined)?.replaced?.source);
      return prev ? S.upload.doneReplacing(file.name, prev.name, prev.asset) : S.upload.done(file.name);
    });
  };
  const reconvert = () => {
    if (!def) return;
    const id = entryKey;
    mutate('reconvert', () => reconvertModel(id), { kind: 'info', text: S.candidate.reconverting }, () => null);
  };
  const remove = async () => {
    if (!def) return;
    const id = entryKey;
    const label = partKey ? `${def.label} ${S.parts.one(partNumber(id))}` : variants.length > 1 ? `${def.label} (${S.variants.one(variantNumber(id))})` : def.label;
    const ok = await mutate('remove', () => removeProp(id), null, () => S.notice.removed(label));
    if (ok) {
      setDraft(DEFAULT_TRANSFORM); // nothing left to place: a dirty draft would only say "unsaved"
      if (partKey) setPart(null); // the part gone: back to its model
      else setVariant(null); // the prop's own model, or the first that remains (see ownerKey)
    }
  };
  const save = async () => {
    if (!def) return;
    setSaving(true);
    try {
      await saveTransform(entryKey, { ...draft, anchor });
      await refresh();
      setNotice({ kind: 'ok', text: S.placement.saved });
    } catch (err) {
      report(err);
    } finally {
      setSaving(false);
    }
  };
  // The prompt's three answers.
  const stay = () => setPendingSwitch(null);
  const discardAndGo = () => {
    if (!pendingSwitch) return;
    setDraft(saved);
    setPendingSwitch(null);
    applySwitch(pendingSwitch.kit, pendingSwitch.propId, pendingSwitch.variant ?? null, pendingSwitch.part ?? null);
  };
  const saveAndGo = async () => {
    if (!def || !pendingSwitch) return;
    const target = pendingSwitch;
    setSaving(true);
    try {
      await saveTransform(entryKey, { ...draft, anchor });
      await refresh();
      setNotice({ kind: 'ok', text: S.placement.saved });
      setPendingSwitch(null);
      applySwitch(target.kit, target.propId, target.variant ?? null, target.part ?? null);
    } catch (err) {
      report(err); // the Save failed: stay, with the work and the reason
      setPendingSwitch(null);
    } finally {
      setSaving(false);
    }
  };
  const change = useCallback((patch: Partial<PropTransform>) => setDraft((d) => ({ ...d, ...patch })), []);
  const nudge = useCallback((dx: number, dz: number) => {
    setDraft((d) => ({ ...d, offset: [tidy(d.offset[0] + dx), d.offset[1], tidy(d.offset[2] + dz)] as Vec3 }));
  }, []);
  /** In-app links (the designer, the estimator) do not fire beforeunload: asked here. */
  const guardLink = (e: React.MouseEvent) => {
    if (hasUnsaved && !window.confirm(S.placement.leaveConfirm)) e.preventDefault();
  };

  // The debug hook reads the latest state through a ref, so its functions
  // stay the same objects for the whole life of the page.
  const snapshot = { spaceType, propId: def?.id ?? '', entryKey, ownerKey, variants, parts, def, mode, draft, saved, dirty, hasUnsaved, pendingSwitch, library, entry, candidateUrl, candidate, notice, busy, saving };
  const latest = useRef(snapshot);
  latest.current = snapshot;
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    const w = window as unknown as { __PROP_STUDIO__?: StudioDebug };
    w.__PROP_STUDIO__ = {
      state: () => {
        const { library: lib, entry: e, def: d, ...rest } = latest.current;
        return { ...rest, def: d ? { id: d.id, label: d.label, size: d.size } : null, libraryLoaded: !!lib, libraryDir: lib?.libraryDir ?? null, entry: e ?? null };
      },
      placedBounds: (id?: string) => {
        const s = latest.current;
        const pid = id ?? s.entryKey;
        const e = s.library?.props[pid];
        if (!e?.modelBounds) return null;
        // The selected model as it is now, unsaved; any other as the library has it.
        return placedBoundsFeet(e.modelBounds, pid === s.entryKey ? { ...s.draft, anchor } : e.transform ?? DEFAULT_TRANSFORM);
      },
      candidatePlacement: () => viewerRef.current?.candidatePlacement() ?? null,
      partPlacement: (key) => viewerRef.current?.partPlacement(key) ?? null,
      screenOfModelPoint: (p) => viewerRef.current?.screenOfModelPoint(p) ?? null,
      frontModelVertex: () => viewerRef.current?.frontModelVertex() ?? null,
      setTransform: (patch) => change(patch),
      modelExtremes: () => viewerRef.current?.modelExtremes() ?? null,
      select: (k, id, v, p) => applySwitch(k, id ?? null, v ?? null, p ?? null),
    };
    return () => { delete w.__PROP_STUDIO__; };
  }, [change, applySwitch]);

  const viewerLine = (() => {
    if (!def) return '';
    if (candidate.phase === 'loading') return S.viewer.loading;
    if (candidate.phase === 'error') return candidate.message;
    if (!entry?.status?.hasModel) return partKey ? S.viewer.noPart(partNumber(partKey)) : S.viewer.noCandidate;
    if (!entry.status.hasGlb) return S.viewer.noPreview;
    return S.viewer.hint;
  })();
  // Under the hint once a candidate is drawn: what the pale plane is, and that
  // the preview's colours are the .glb's, not the materials Cycles will use.
  const viewerNote = candidate.phase === 'ready'
    ? [modelIsBlend ? S.viewer.colourNote : '', S.viewer.wallHint].filter(Boolean).join(' · ')
    : '';
  const noticeColour = notice?.kind === 'error' ? '#ff9f9f' : notice?.kind === 'ok' ? '#9fe0b0' : '#ffd98a';
  // The hold and hang markers shown or not (the viewer's gold ball and blue ring), kept across visits.
  const [showMarkers, setShowMarkers] = useState<boolean>(() => { try { return window.localStorage.getItem(MARKERS_KEY) !== '0'; } catch { return true; } });
  const toggleMarkers = () => setShowMarkers((on) => { try { window.localStorage.setItem(MARKERS_KEY, on ? '0' : '1'); } catch { /* not kept — fine */ } return !on; });
  const modeButton = (m: ViewerMode, label: string, title: string) => (
    <button
      type="button" onClick={() => setMode(m)} title={title} aria-pressed={mode === m}
      style={{ ...control, cursor: 'pointer', ...(mode === m ? { background: ACCENT, color: '#000', border: `1px solid ${ACCENT}`, fontWeight: 700 } : {}) }}
    >
      {label}
    </button>
  );
  const pendingTarget = pendingSwitch
    ? (pendingSwitch.propId ? studioProps(pendingSwitch.kit, catalogue).find((p) => p.id === pendingSwitch.propId)?.label : null) ?? kitName(pendingSwitch.kit)
    : '';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', flexDirection: 'column', background: '#1a1a1e', color: '#fff' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0.5rem 1rem', background: 'rgba(0,0,0,0.4)', flex: '0 0 auto', fontSize: '0.8rem' }}>
        <strong style={{ fontSize: 14, whiteSpace: 'nowrap' }}>{S.title}</strong>
        <div role="tablist" style={{ display: 'flex', gap: 4 }}>
          {(['props', 'finishes', 'looks'] as const).map((k) => (
            <button
              key={k} type="button" role="tab" aria-selected={section === k} title={S.sectionsTitle[k]} onClick={() => chooseSection(k)}
              style={{ ...control, cursor: 'pointer', ...(section === k ? { background: ACCENT, color: '#000', border: `1px solid ${ACCENT}`, fontWeight: 700 } : {}) }}
            >
              {S.sections[k]}
            </button>
          ))}
        </div>
        {section === 'props' && <span style={{ color: MUTED, whiteSpace: 'nowrap' }}>{S.subtitle}</span>}
        {section === 'props' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0, whiteSpace: 'nowrap' }}>
          {S.spaceType}
          <select value={spaceType} onChange={(e) => requestSwitch(e.target.value, null)} style={control}>
            {KIT_KEYS.map((k) => <option key={k} value={k} style={{ color: '#000' }}>{kitName(k)}</option>)}
          </select>
        </label>
        )}
        <div role="status" style={{ flex: 1, minWidth: 0, color: noticeColour, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={notice?.text}>
          {notice?.text ?? (library ? '' : S.notice.loadingLibrary)}
        </div>
        <Link to="/sandbox/designer" onClick={guardLink} style={{ color: '#cfcfd6', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none', whiteSpace: 'nowrap' }}>
          <DraftingCompass size={13} /> {S.designer}
        </Link>
        <Link to="/" onClick={guardLink} style={{ color: '#cfcfd6', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none', whiteSpace: 'nowrap' }}>
          <ArrowLeft size={13} /> {S.backToEstimator}
        </Link>
      </header>

      {/* Floors & lighting by space; the props stay mounted under it, so an unsaved placement survives a look across. */}
      {section === 'finishes' && <FinishesManager />}
      {section === 'looks' && <LookLab />}
      <div style={{ flex: 1, display: section === 'props' ? 'flex' : 'none', minHeight: 0, position: 'relative' }}>
        <aside style={{ width: 230, flex: '0 0 auto', overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.1)', padding: 10 }}>
          <div style={LABEL}>{S.props}</div>
          {listed.map((p) => {
            const active = def?.id === p.id;
            const status = propStatusAcross(library, p.id);
            const colours = BADGE_COLOURS[status];
            return (
              <button
                key={p.id} type="button" aria-label={p.label} aria-pressed={active} onClick={() => requestSwitch(spaceType, p.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', textAlign: 'left', padding: '6px 8px', marginBottom: 2,
                  borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#fff', background: active ? 'rgba(254,177,0,0.18)' : 'transparent',
                  border: active ? `1px solid ${ACCENT}` : '1px solid transparent',
                }}
              >
                <span>
                  {p.label}
                  {p.kind !== 'floor' && <span title={S.kindTagTitle} style={{ marginLeft: 6, fontSize: 10, color: MUTED }}>{S.kindTag[p.kind] ?? p.kind}</span>}
                  {catalogue?.[p.id]?.added && <span style={{ marginLeft: 6, fontSize: 10, color: '#9fe0b0' }}>{S.madeTag}</span>}
                  {catalogue?.[p.id]?.disabled && <span title={S.offTagTitle} style={{ marginLeft: 6, fontSize: 10, color: '#ff9f9f' }}>{S.offTag}</span>}
                </span>
                {library && (
                  <span title={S.statusTitle[status]} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, border: `1px solid ${colours.border}`, color: colours.fg, whiteSpace: 'nowrap' }}>
                    {S.status[status]}
                  </span>
                )}
              </button>
            );
          })}
          {!library && <div style={{ marginTop: 8, fontSize: 11, color: MUTED }}>{S.libraryOffline}</div>}
          {library && spaceType !== 'fixtures' && (
            <button
              type="button" disabled={catBusy} title={S.catalogue.newPropTitle} onClick={newProp}
              style={{ ...control, cursor: 'pointer', width: '100%', marginTop: 8, padding: '5px 8px', fontSize: 12, color: '#9fe0b0' }}
            >
              {S.catalogue.newProp}
            </button>
          )}
        </aside>

        <main style={{ flex: 1, position: 'relative', minWidth: 0 }}>
          {def && (
            <PropViewer
              ref={viewerRef} def={shownDef ?? def} mode={mode} onNudge={nudge} onCandidate={setCandidate}
              candidateUrl={ownerUrl} modelBounds={ownerEntry?.modelBounds ?? null}
              transform={partKey ? ownerEntry?.transform ?? DEFAULT_TRANSFORM : { ...draft, anchor: shownAnchor }}
              parts={viewerParts}
              showHang={isHeld} showMarkers={showMarkers} picking={isHeld && picking}
              onPick={(hang) => { change({ hang }); setPicking(false); }}
              onCancelPick={() => setPicking(false)}
            />
          )}
          <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6 }}>
            {modeButton('side', S.viewer.sideBySide, S.viewer.sideBySideTitle)}
            {modeButton('overlay', S.viewer.overlay, S.viewer.overlayTitle)}
            {/* The camera on the model alone, or back on the whole arrangement. */}
            <button type="button" style={{ ...control, cursor: 'pointer', marginLeft: 8 }} title={S.viewer.centreModelTitle} disabled={!candidateUrl} onClick={() => viewerRef.current?.focusModel()}>
              {S.viewer.centreModel}
            </button>
            <button type="button" style={{ ...control, cursor: 'pointer' }} title={S.viewer.centreAllTitle} onClick={() => viewerRef.current?.focusAll()}>
              {S.viewer.centreAll}
            </button>
            {/* The hold and hang markers off, to see the model unobstructed while placing it. Remembered. */}
            <button
              type="button" aria-pressed={showMarkers} title={S.viewer.markersTitle} onClick={() => toggleMarkers()}
              style={{ ...control, cursor: 'pointer', marginLeft: 8, ...(showMarkers ? {} : { opacity: 0.6 }) }}
            >
              {showMarkers ? S.viewer.markersOn : S.viewer.markersOff}
            </button>
          </div>
          <div style={{ position: 'absolute', top: 10, right: 10, fontSize: 12, color: '#fff', background: 'rgba(0,0,0,0.45)', padding: '4px 8px', borderRadius: 6, pointerEvents: 'none' }}>
            {def ? `${def.label} · ${def.size.w} × ${def.size.d} × ${def.size.h} ft` : ''}
          </div>
          <div style={{ position: 'absolute', bottom: 8, left: 10, right: 10, fontSize: 11, color: candidate.phase === 'error' ? '#ff9f9f' : MUTED, pointerEvents: 'none', textAlign: 'center' }}>
            <div>{viewerLine}</div>
            {viewerNote && <div className="prop-studio-viewer-note" style={{ marginTop: 2, color: '#b9bcc6' }}>{viewerNote}</div>}
          </div>
        </main>

        <aside style={{ width: 380, flex: '0 0 auto', overflowY: 'auto', borderLeft: '1px solid rgba(255,255,255,0.1)', padding: 10 }}>
          {/* The prop's models: one chip each, and one more to add another. A
              render picks one per staged copy; the designer stages the prop
              round the largest. */}
          {def && library && (
            <section style={{ marginBottom: 10, padding: '8px 10px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }} aria-label={S.variants.title}>
              <div style={{ ...LABEL, margin: '0 0 6px' }} title={S.variants.hint}>{S.variants.title}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {/* The prop's models in order, and — only while one is being added — the new slot after them. */}
                {(variants.includes(ownerKey) ? variants : [...variants, ownerKey]).sort((a, b) => variantNumber(a) - variantNumber(b)).map((key) => {
                  const n = variantNumber(key);
                  const isNew = !library.props[key];
                  const st = isNew ? 'none' : propStatus(library.props[key]);
                  const on = key === ownerKey; // the model in hand — also while one of its parts is being placed
                  return (
                    <button
                      key={key} type="button" aria-pressed={on} title={isNew ? S.variants.fresh : S.statusTitle[st]}
                      onClick={() => requestSwitch(spaceType, def.id, key === def.id ? null : key, null)}
                      style={{
                        ...control, cursor: 'pointer', padding: '3px 9px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
                        ...(on ? { background: 'rgba(254,177,0,0.18)', border: `1px solid ${ACCENT}` } : {}),
                      }}
                    >
                      {S.variants.one(n)}
                      <span style={{ width: 7, height: 7, borderRadius: 4, background: BADGE_COLOURS[st].fg, opacity: isNew ? 0.4 : 1 }} />
                    </button>
                  );
                })}
                {variants.includes(ownerKey) && (
                  <button
                    type="button" title={S.variants.addTitle} style={{ ...control, cursor: 'pointer', padding: '3px 9px', fontSize: 12, color: '#9fe0b0' }}
                    onClick={() => requestSwitch(spaceType, def.id, nextVariantKey(library, def.id), null)}
                  >
                    {S.variants.add}
                  </button>
                )}
              </div>
              {/* The model's parts — models placed on it — and one more to add. */}
              {ownerEntry?.status?.hasModel && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }} aria-label={S.parts.title(S.variants.one(variantNumber(ownerKey)))}>
                  <span style={{ fontSize: 11, color: MUTED }} title={S.parts.hint}>{S.parts.title(S.variants.one(variantNumber(ownerKey)))}</span>
                  {(parts.includes(entryKey) || !partKey ? parts : [...parts, entryKey]).map((key) => {
                    const isNew = !library.props[key];
                    const st = isNew ? 'none' : propStatus(library.props[key]);
                    const on = key === entryKey;
                    return (
                      <button
                        key={key} type="button" aria-pressed={on} title={isNew ? S.variants.fresh : S.statusTitle[st]}
                        onClick={() => requestSwitch(spaceType, def.id, chosen, key)}
                        style={{
                          ...control, cursor: 'pointer', padding: '3px 9px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
                          ...(on ? { background: 'rgba(254,177,0,0.18)', border: `1px solid ${ACCENT}` } : {}),
                        }}
                      >
                        {S.parts.one(partNumber(key))}
                        <span style={{ width: 7, height: 7, borderRadius: 4, background: BADGE_COLOURS[st].fg, opacity: isNew ? 0.4 : 1 }} />
                      </button>
                    );
                  })}
                  {(!partKey || parts.includes(entryKey)) && (
                    <button
                      type="button" title={S.parts.addTitle} style={{ ...control, cursor: 'pointer', padding: '3px 9px', fontSize: 12, color: '#9fe0b0' }}
                      onClick={() => requestSwitch(spaceType, def.id, chosen, nextPartKey(library, ownerKey))}
                    >
                      {S.parts.add}
                    </button>
                  )}
                </div>
              )}
            </section>
          )}
          {def && library && fixture && (
            <p style={{ margin: '0 0 10px', fontSize: 11, color: MUTED }}>{S.catalogue.fixtureNote}</p>
          )}
          {def && library && !fixture && !partKey && (
            <div style={{ marginBottom: 10 }}>
              <PlacesPanel
                def={def} catalogue={catalogue} previewPlace={shownPlace} onPreviewPlace={setPreviewPlace}
                onSave={saveEntry} onDelete={deleteProp} busy={catBusy || saving} modelSize={modelSize} onMatchModel={matchModel}
              />
            </div>
          )}
          {def && (
            <CandidatePanel
              def={def} entryKey={entryKey} entry={entry} libraryLoaded={!!library} libraryDir={library?.libraryDir} busy={busy}
              onAssign={assign} onUpload={upload} onReconvert={reconvert} onRemove={remove} onError={report}
            >
              <PlacementPanel
                def={shownDef ?? def} modelBounds={modelBounds} draft={{ ...draft, anchor: partKey ? 'floor' : shownAnchor }} dirty={dirty} saving={saving} candidate={candidate}
                simple={fixture}
                onChange={change} onSave={save} onReset={() => setDraft(saved)}
                onRestOnFloor={() => {
                  // The viewer measures the drawn mesh; the Up offset drops by its lowest point.
                  const ex = viewerRef.current?.modelExtremes();
                  if (!ex) return;
                  setDraft((d) => ({ ...d, offset: [d.offset[0], Math.round((d.offset[1] - ex.minY) * 1000) / 1000, d.offset[2]] }));
                }}
                onFlushToWall={() => {
                  // The mesh's nearest point onto the wall line (the frame's wall is at wallZ).
                  const ex = viewerRef.current?.modelExtremes();
                  if (!ex || !shownDef) return;
                  const wallZ = stagedFrame(shownDef, modelBounds, { ...draft, anchor: partKey ? 'floor' : shownAnchor }).wallZ;
                  setDraft((d) => ({ ...d, offset: [d.offset[0], d.offset[1], Math.round((d.offset[2] - (ex.minZ - wallZ)) * 1000) / 1000] }));
                }}
                partOf={partKey ? S.variants.one(variantNumber(ownerKey)) : undefined}
                picking={picking}
                onTogglePick={() => {
                  setPicking((p) => !p);
                  // The viewer takes the keys while picking, so Esc reaches it.
                  document.querySelector<HTMLElement>('.prop-studio-viewer')?.focus({ preventScroll: true });
                }}
              />
            </CandidatePanel>
          )}
        </aside>

        {pendingSwitch && def && (
          <div
            role="dialog" aria-modal="true" aria-label={S.placement.unsavedTitle}
            style={{ position: 'absolute', inset: 0, zIndex: 5, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div style={{ background: '#26282e', border: `1px solid ${ACCENT}`, borderRadius: 10, padding: '16px 18px', maxWidth: 440, boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{S.placement.unsavedTitle}</div>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#cfcfd6' }}>{S.placement.unsavedBody(def.label, pendingTarget)}</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" style={{ ...control, cursor: 'pointer' }} onClick={stay} disabled={saving}>{S.placement.stay}</button>
                <button type="button" style={{ ...control, cursor: 'pointer' }} onClick={discardAndGo} disabled={saving}>{S.placement.discard}</button>
                <button type="button" style={{ ...control, background: ACCENT, color: '#000', border: 'none', fontWeight: 700, cursor: 'pointer' }} onClick={saveAndGo} disabled={saving}>
                  {saving ? S.placement.saving : S.placement.saveAndGo}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropStudio;
